/**
 * StoryBookLearning.jsx  —  NISH FSLP
 * ─────────────────────────────────────────────────────────────────────────────
 * Probes active in all four modes (Read Aloud, Picture Talk, Retell, Word Hunt).
 * Five literacy-focused probe types cycle per page/checkpoint.
 * One probe per page per turn. Same 3-button rating row.
 * Backend: `signals: [{ probe, level }]` added to PATCH /flashcard-sessions/:id/review.
 *
 * Probe types (story/literacy focused, distinct from flashcard item probes):
 *   comprehension → can they say what this page is about?
 *   vocabulary    → can they explain a key word from this page?
 *   inference     → can they predict what happens next?
 *   narration     → can they retell this part in their own words?
 *   connection    → can they connect this to something in their own life?
 *
 * Probe targets:
 *   ReadAloudMode   → current page (pageIdx % PROBES.length)
 *   PictureTalkMode → current page (pageIdx % PROBES.length)
 *   RetellMode      → current checkpoint's section page (cpIdx % PROBES.length)
 *   WordHuntMode    → current page (pageIdx % PROBES.length), slide-up panel
 */

import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

// ─── Modes ────────────────────────────────────────────────────────────────────
const MODES = [
  { id:"read-aloud",   icon:"📖", label:"Read Aloud",       desc:"Teacher reads each page — child listens and follows",        color:"#8B4513", bg:"#FFFDF5" },
  { id:"picture-talk", icon:"🗣️", label:"Picture Talk",      desc:"Image only — child describes what they see",                color:"#1565C0", bg:"#EEF8FF" },
  { id:"retell",       icon:"🔄", label:"Retell the Story",  desc:"After reading, child retells the story in their own words",  color:"#2E7D32", bg:"#F0FFF4" },
  { id:"word-hunt",    icon:"🔍", label:"Word Hunt",         desc:"Teacher calls a word — child finds it on the page",          color:"#C2185B", bg:"#FFF0F8" },
];

const LANGUAGES = [
  { code:"en", name:"English",  voice:"en-IN" },
  { code:"ml", name:"മലയാളം", voice:"ml-IN" },
  { code:"hi", name:"हिंदी",   voice:"hi-IN" },
  { code:"ta", name:"தமிழ்",  voice:"ta-IN" },
  { code:"te", name:"తెలుగు", voice:"te-IN" },
];

// ─── Five literacy probe types ────────────────────────────────────────────────
// Cycle by page/checkpoint index. Story-focused — distinct from item probes
// in FlashcardLearning and ActivityCardLearning.
// vocabulary probe receives a key word extracted from page text via extractWords().
const PROBES = [
  {
    id: "comprehension",
    title: "Comprehension",
    question: () => "Can they say what this page is about?",
    hint: "Retell this page in 1–2 sentences — basic understanding",
  },
  {
    id: "vocabulary",
    title: "Vocabulary",
    question: (word) => word
      ? `Can they explain what "${word}" means?`
      : "Can they explain a key word from this page?",
    hint: "Word meaning — receptive or expressive vocabulary",
  },
  {
    id: "inference",
    title: "Inference",
    question: () => "Can they predict what happens next?",
    hint: "Prediction / inference — reading between the lines",
  },
  {
    id: "narration",
    title: "Narrate",
    question: () => "Can they retell this part in their own words?",
    hint: "Expressive retelling — sequence and detail",
  },
  {
    id: "connection",
    title: "Connection",
    question: () => "Can they connect this page to something in their own life?",
    hint: "Text-to-self connection — personal relevance",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
async function apiFetch(method, path, body, headers) {
  const h = { "Content-Type":"application/json", ...(headers||{}) };
  const res = await fetch(`${API}${path}`, {
    method, headers:h,
    body: body ? JSON.stringify(body) : undefined,
  });
  const t = await res.text();
  return t ? JSON.parse(t) : {};
}

async function closeSession(sessionId, getAuthHeader, startMs) {
  if (!sessionId) return;
  try {
    await apiFetch(
      "PUT",
      `/flashcard-sessions/${sessionId}/close`,
      { durationMin: Math.round((Date.now() - (startMs||Date.now())) / 60000) },
      getAuthHeader()
    );
  } catch {}
}

function speak(text, langCode) {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const lang = LANGUAGES.find(l=>l.code===langCode) || LANGUAGES[0];
  const utt  = new SpeechSynthesisUtterance(text);
  utt.lang   = lang.voice;
  utt.rate   = 0.8;
  utt.pitch  = 1.0;
  window.speechSynthesis.speak(utt);
}

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("http")) return url;
  return `${API.replace("/api","")}${url}`;
}

function getPageText(page, language) {
  return page?.texts?.[language] || page?.texts?.en || "";
}

function extractWords(text) {
  if (!text) return [];
  const words = text.match(/\b[a-zA-Z\u0900-\u097F\u0D00-\u0D7F\u0B80-\u0BFF\u0C00-\u0C7F]{3,}\b/g) || [];
  const unique = [...new Set(words.map(w=>w.toLowerCase()))];
  return unique.slice(0, 12);
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",
                 padding:60,flexDirection:"column",gap:12}}>
      <div style={{width:28,height:28,border:"3px solid #e0e7f0",
                   borderTop:"3px solid #003366",borderRadius:"50%",
                   animation:"spin .7s linear infinite"}}/>
      <div style={{fontSize:13,color:"#aaa"}}>Loading…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ModeBar({ icon, title, subtitle, current, total, color="#003366", onExit }) {
  const pct = total>0 ? (current/total)*100 : 0;
  return (
    <>
      <div style={{background:"white",borderBottom:"1px solid #dce3f5",padding:"10px 20px",
                   display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <button onClick={onExit}
          style={{background:"#f0f4ff",border:"none",borderRadius:8,padding:"7px 14px",
                  cursor:"pointer",fontSize:12,color:"#666",fontWeight:700}}>← Exit</button>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:13,color}}>{icon} {title}</div>
          {subtitle&&<div style={{fontSize:11,color:"#888"}}>{subtitle}</div>}
        </div>
        {total>0&&<div style={{fontSize:12,color:"#888",fontWeight:600}}>Page {current} / {total}</div>}
      </div>
      <div style={{height:5,background:"#dce3f5",flexShrink:0}}>
        <div style={{width:`${pct}%`,height:"100%",background:color,transition:"width .4s"}}/>
      </div>
    </>
  );
}

function TeacherRating({ onRate, disabled=false, label="How did the child respond?" }) {
  return (
    <div style={{marginTop:20,width:"min(480px,92vw)"}}>
      <div style={{fontSize:11,fontWeight:700,color:"#888",textAlign:"center",
                   textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>
        {label}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
        {[
          {q:1,label:"Needs Help",  emoji:"🔴",color:"#C0392B",bg:"#FDEDEC"},
          {q:3,label:"With Prompt", emoji:"🟡",color:"#D4AC0D",bg:"#FEF9E7"},
          {q:5,label:"Got It! ✓",   emoji:"🟢",color:"#1B6B45",bg:"#EDF7F2"},
        ].map(r=>(
          <button key={r.q} onClick={()=>onRate(r.q)} disabled={disabled}
            style={{padding:"18px 8px",background:r.bg,border:`2.5px solid ${r.color}`,
                    borderRadius:16,cursor:"pointer",fontWeight:800,fontSize:13,
                    color:r.color,display:"flex",flexDirection:"column",
                    alignItems:"center",gap:6,opacity:disabled?0.5:1,minHeight:72}}>
            <span style={{fontSize:28}}>{r.emoji}</span>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function PageImage({ page, language, height=320, showText=false, compact=false }) {
  const src  = resolveUrl(page?.imageUrl);
  const text = getPageText(page, language);
  return (
    <div style={{width:compact?"min(300px,80vw)":"min(520px,94vw)",background:"white",
                 borderRadius:compact?12:20,overflow:"hidden",
                 boxShadow:"0 8px 40px rgba(0,0,0,.12)",border:"2px solid #e0e7f0"}}>
      {src
        ? <div style={{height,background:"#f8f9ff",display:"flex",
                       alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
            <img src={src} alt="" style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
          </div>
        : <div style={{height,background:"#f0f4ff",display:"flex",
                       alignItems:"center",justifyContent:"center",fontSize:48,color:"#ccd"}}>📄</div>
      }
      {showText && text && (
        <div style={{padding:compact?"10px 14px":"18px 22px",
                     fontSize:compact?12:16,fontFamily:"Georgia,serif",
                     lineHeight:1.8,color:"#2a1a0a",borderTop:"2px solid #f0e8d8",
                     background:"#fffdf8"}}>
          {text}
        </div>
      )}
    </div>
  );
}

// ─── ProbeRound ───────────────────────────────────────────────────────────────
// Shared by all four storybook modes.
// probe     = PROBES[idx % PROBES.length]
// page      = current page (shown as thumbnail)
// language  = for reading text
// probeWord = key word (only used by vocabulary probe)
// onRate(level) = called with "absent" | "emerging" | "strong"
function ProbeRound({ probe, page, language, probeWord, onRate, submitting }) {
  const src      = resolveUrl(page?.imageUrl);
  const question = probe.id === "vocabulary"
    ? probe.question(probeWord)
    : probe.question();

  return (
    <div style={{width:"min(480px,92vw)"}}>
      {/* Probe question box with page thumbnail */}
      <div style={{background:"#EEF8FF", borderRadius:12, padding:"13px 16px",
                   border:"1.5px solid #90CAF9", marginBottom:14,
                   display:"flex", alignItems:"flex-start", gap:14}}>
        {src && (
          <div style={{width:60, height:60, borderRadius:9, overflow:"hidden",
                       border:"2px solid #90CAF9", flexShrink:0,
                       background:"#f0f7ff", display:"flex", alignItems:"center",
                       justifyContent:"center"}}>
            <img src={src} alt="" style={{maxWidth:"100%", maxHeight:"100%", objectFit:"contain"}}/>
          </div>
        )}
        <div style={{flex:1}}>
          <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:5}}>
            <span style={{fontSize:10, fontWeight:800, color:"#1565C0", letterSpacing:.8,
                           textTransform:"uppercase", background:"#BBDEFB",
                           padding:"2px 8px", borderRadius:6}}>
              {probe.title}
            </span>
          </div>
          <div style={{fontWeight:700, fontSize:13, color:"#0D47A1", lineHeight:1.5, marginBottom:3}}>
            {question}
          </div>
          <div style={{fontSize:11, color:"#5C7A9E", fontStyle:"italic"}}>
            {probe.hint}
          </div>
        </div>
      </div>
      {/* Rating buttons */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10}}>
        {[
          { level:"absent",   label:"Needs Help",  emoji:"🔴", color:"#C0392B", bg:"#FDEDEC" },
          { level:"emerging", label:"With Prompt", emoji:"🟡", color:"#D4AC0D", bg:"#FEF9E7" },
          { level:"strong",   label:"Got It! ✓",   emoji:"🟢", color:"#1B6B45", bg:"#EDF7F2" },
        ].map(r => (
          <button key={r.level} onClick={() => onRate(r.level)} disabled={submitting}
            style={{padding:"16px 8px", background:r.bg, border:`2.5px solid ${r.color}`,
                     borderRadius:16, cursor:"pointer", fontWeight:800, fontSize:13,
                     color:r.color, display:"flex", flexDirection:"column",
                     alignItems:"center", gap:6, opacity:submitting ? 0.5 : 1, minHeight:70}}>
            <span style={{fontSize:26}}>{r.emoji}</span>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function StarBurst({ count=3 }) {
  return (
    <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:900,overflow:"hidden"}}>
      {Array.from({length:count*8}).map((_,i)=>{
        const x=Math.random()*100, dur=0.8+Math.random()*0.6, delay=Math.random()*0.4;
        const color=["#FFD700","#8B4513","#1B6B45","#003366","#C2185B","#E65100"][i%6];
        return (
          <div key={i} style={{position:"absolute",left:`${x}%`,top:"60%",
            width:10,height:10,borderRadius:"50%",background:color,
            animation:`sb_burst${i%2===0?"Up":"Diag"} ${dur}s ease-out ${delay}s forwards`,opacity:0}}/>
        );
      })}
      <style>{`
        @keyframes sb_burstUp   {0%{opacity:1;transform:translateY(0) scale(1)}100%{opacity:0;transform:translateY(-300px) scale(.4)}}
        @keyframes sb_burstDiag {0%{opacity:1;transform:translate(0,0) scale(1)}100%{opacity:0;transform:translate(110px,-260px) scale(.3)}}
      `}</style>
    </div>
  );
}

function CelebrationScreen({ correct, total, childName, bookTitle, onReplay, onExit }) {
  const pct   = total>0 ? Math.round((correct/total)*100) : 100;
  const stars = pct>=85 ? 3 : pct>=60 ? 2 : 1;
  const msgs  = ["Keep practising — you'll get there! 💪","Good effort! 🌟","Excellent reading! 🎉"];
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#3a1505 0%,#5c2010 50%,#003366 100%)",
                 display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <StarBurst count={stars}/>
      <div style={{background:"white",borderRadius:24,padding:"40px 32px",maxWidth:440,width:"100%",
                   textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.4)",position:"relative",zIndex:10}}>
        <div style={{fontSize:48,marginBottom:8,letterSpacing:4}}>
          {Array.from({length:3}).map((_,i)=>(
            <span key={i} style={{opacity:i<stars?1:0.2}}>⭐</span>
          ))}
        </div>
        <div style={{fontFamily:"Georgia,serif",fontSize:26,fontWeight:900,color:"#8B4513",marginBottom:4}}>
          {stars===3?"Wonderful!":stars===2?"Well done!":"Good try!"}
        </div>
        {childName&&<div style={{fontSize:14,color:"#888",marginBottom:2}}>{childName}</div>}
        {bookTitle&&<div style={{fontSize:11,color:"#aaa",marginBottom:16,fontStyle:"italic"}}>"{bookTitle}"</div>}
        <div style={{fontSize:13,color:"#555",marginBottom:28,lineHeight:1.6}}>{msgs[stars-1]}</div>
        <div style={{display:"flex",justifyContent:"center",gap:20,marginBottom:28}}>
          {[
            {label:"Score",   val:`${pct}%`,color:pct>=85?"#1B6B45":pct>=60?"#D4AC0D":"#C0392B"},
            {label:"Correct", val:correct,  color:"#1B6B45"},
            {label:"Total",   val:total,    color:"#8B4513"},
          ].map(s=>(
            <div key={s.label} style={{textAlign:"center"}}>
              <div style={{fontSize:28,fontWeight:900,color:s.color}}>{s.val}</div>
              <div style={{fontSize:10,color:"#aaa",fontWeight:600,textTransform:"uppercase"}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {onReplay&&(
            <button onClick={onReplay}
              style={{padding:"13px",background:"#8B4513",color:"white",border:"none",
                      borderRadius:12,fontWeight:700,fontSize:14,cursor:"pointer",
                      boxShadow:"0 4px 16px rgba(139,69,19,.3)"}}>
              🔄 Read Again
            </button>
          )}
          <button onClick={onExit}
            style={{padding:"13px",background:"white",color:"#666",
                    border:"1.5px solid #dce3f5",borderRadius:12,
                    fontWeight:700,fontSize:14,cursor:"pointer"}}>
            ← Back to Setup
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionNotesScreen({ sessionId, getAuthHeader, startMs, onDone, childName, score }) {
  const [notes,  setNotes]  = useState("");
  const [gen,    setGen]    = useState("");
  const [saving, setSaving] = useState(false);
  const scoreColor = score >= 85 ? "#1B6B45" : score >= 60 ? "#D4AC0D" : "#C0392B";
  const scoreBg    = score >= 85 ? "#EDF7F2" : score >= 60 ? "#FEF9E7" : "#FDEDEC";

  async function save() {
    setSaving(true);
    try {
      await apiFetch("PUT", `/flashcard-sessions/${sessionId}/close`, {
        durationMin:        Math.round((Date.now() - (startMs || Date.now())) / 60000),
        sessionNotes:       notes,
        generalizationNote: gen,
      }, getAuthHeader());
    } catch {}
    setSaving(false);
    onDone();
  }

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#003366 0%,#1a3a6e 60%,#2d1b5e 100%)",
                 display:"flex",alignItems:"center",justifyContent:"center",padding:24,
                 fontFamily:"'Segoe UI',Arial,sans-serif"}}>
      <div style={{background:"white",borderRadius:22,padding:"34px 30px",width:500,
                   maxWidth:"96vw",boxShadow:"0 24px 80px rgba(0,0,0,.4)"}}>
        <div style={{textAlign:"center",marginBottom:26}}>
          <div style={{fontSize:40,marginBottom:10}}>📝</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:21,fontWeight:900,
                       color:"#1a1a2e",marginBottom:8}}>Session Notes</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
            {childName&&<span style={{fontSize:13,color:"#666",fontWeight:600}}>{childName}</span>}
            {childName&&<span style={{color:"#ddd"}}>·</span>}
            <span style={{fontSize:13,fontWeight:800,color:scoreColor,background:scoreBg,
                           padding:"2px 12px",borderRadius:20,border:`1px solid ${scoreColor}40`}}>
              {score}% score
            </span>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",
                       letterSpacing:.8,marginBottom:6}}>Session Observations</div>
          <textarea autoFocus value={notes} onChange={e=>setNotes(e.target.value)} rows={3}
            placeholder="Attention span, engagement, behaviour, fatigue, prompts needed…"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:10,
                    padding:"10px 13px",fontSize:14,outline:"none",resize:"vertical",
                    boxSizing:"border-box",fontFamily:"inherit",lineHeight:1.6}}
            onFocus={e=>e.target.style.borderColor="#003366"}
            onBlur={e=>e.target.style.borderColor="#dce3f5"}/>
        </div>
        <div style={{marginBottom:26}}>
          <div style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",
                       letterSpacing:.8,marginBottom:6}}>
            Generalization Note <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span>
          </div>
          <textarea value={gen} onChange={e=>setGen(e.target.value)} rows={2}
            placeholder="Did the child apply any learned concepts outside of this session?"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:10,
                    padding:"10px 13px",fontSize:14,outline:"none",resize:"vertical",
                    boxSizing:"border-box",fontFamily:"inherit",lineHeight:1.6}}
            onFocus={e=>e.target.style.borderColor="#003366"}
            onBlur={e=>e.target.style.borderColor="#dce3f5"}/>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={onDone}
            style={{flex:1,padding:"12px",background:"white",border:"1.5px solid #dce3f5",
                    borderRadius:11,color:"#666",fontWeight:700,fontSize:14,cursor:"pointer"}}>
            Skip
          </button>
          <button onClick={save} disabled={saving}
            style={{flex:2,padding:"12px",background:saving?"#aaa":"#003366",
                    color:"white",border:"none",borderRadius:11,fontWeight:700,fontSize:14,
                    cursor:saving?"default":"pointer",
                    boxShadow:saving?"none":"0 4px 16px rgba(0,51,102,.3)"}}>
            {saving?"Saving…":"✓ Save & Exit"}
          </button>
        </div>
        <div style={{textAlign:"center",marginTop:12,fontSize:11,color:"#aaa"}}>
          Notes are saved to the child's session log in Progress Tracker
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 1 — READ ALOUD  (with probe after each page rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per page:
//   1. Page shown with text + Read Aloud button → teacher reads, child follows
//   2. Teacher rates "how well did child follow?" (phase: "rate")
//      → stores quality, stops speech, moves to "probe" phase
//   3. Compact page thumbnail + ProbeRound shown
//   4. Teacher rates probe → both posted with signals[], advance to next page
//
function ReadAloudMode({ pages, language, sessionId, getAuthHeader, childName, bookTitle, onDone }) {
  const m            = MODES[0];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const [showNotes,      setShowNotes]      = useState(false);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [history, setHistory] = useState([]);

  const page  = pages[pageIdx];
  const text  = getPageText(page, language);
  const done  = pageIdx >= pages.length;
  const probe = PROBES[pageIdx % PROBES.length];
  // Vocabulary probe extracts a key word from the page text
  const probeWord = probe.id === "vocabulary" ? (extractWords(text)[0] || "") : "";

  useEffect(()=>{
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  },[done]);

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    window.speechSynthesis?.cancel();
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     page.pageId || page._id || String(pageIdx),
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setPageIdx(i => i + 1);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = pages.length>0?Math.round((correct/pages.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={pages.length} childName={childName} bookTitle={bookTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setPageIdx(0); setHistory([]); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:m.bg,display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Read Aloud" subtitle={childName} color={m.color} current={pageIdx+1} total={pages.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px",gap:16}}>

        {phase === "rate" && (
          <>
            <div style={{fontSize:11,fontWeight:700,color:m.color,textTransform:"uppercase",letterSpacing:1}}>
              {bookTitle} — Page {pageIdx+1}
            </div>
            <PageImage page={page} language={language} height={260} showText={true}/>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <button onClick={()=>speak(text, language)}
                style={{background:"white",border:`2px solid ${m.color}`,borderRadius:24,
                        padding:"8px 20px",fontSize:13,color:m.color,fontWeight:700,
                        cursor:"pointer",boxShadow:`0 2px 12px ${m.color}22`}}>
                🔊 Read Aloud
              </button>
              <button onClick={()=>window.speechSynthesis?.cancel()}
                style={{background:"#f0f4ff",border:"1.5px solid #dce3f5",borderRadius:24,
                        padding:"8px 16px",fontSize:12,color:"#888",fontWeight:600,cursor:"pointer"}}>
                ⏹ Stop
              </button>
            </div>
            <TeacherRating onRate={handleQualityRate} disabled={submRef.current} label="How well did the child follow along?"/>
          </>
        )}

        {phase === "probe" && (
          <>
            {/* Compact page thumbnail stays visible for context */}
            <PageImage page={page} language={language} height={140} showText={false} compact={true}/>
            <ProbeRound
              probe={probe}
              page={page}
              language={language}
              probeWord={probeWord}
              onRate={handleProbeRate}
              submitting={submRef.current}
            />
          </>
        )}

        {/* Progress dots */}
        <div style={{display:"flex",gap:7,marginTop:4}}>
          {pages.map((_,i)=>(
            <div key={i} style={{width:10,height:10,borderRadius:"50%",
                                 background:i<history.length?(history[i]?.correct?"#1B6B45":"#C0392B"):i===pageIdx?m.color:"#e2e8f0",
                                 transition:"background .3s"}}/>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 2 — PICTURE TALK  (with probe after each page rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per page:
//   1. Image shown (no text) → child describes freely
//   2. Teacher rates "how well did child describe?" (phase: "rate")
//      → stores quality, moves to "probe" phase
//   3. Same image (compact) + ProbeRound shown
//   4. Teacher rates probe → both posted with signals[], advance
//
function PictureTalkMode({ pages, language, sessionId, getAuthHeader, childName, bookTitle, onDone }) {
  const m            = MODES[1];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const [showNotes,      setShowNotes]      = useState(false);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);
  const [pageIdx,   setPageIdx]   = useState(0);
  const [showText,  setShowText]  = useState(false);
  const [history,   setHistory]   = useState([]);

  const page  = pages[pageIdx];
  const text  = getPageText(page, language);
  const done  = pageIdx >= pages.length;
  const probe = PROBES[pageIdx % PROBES.length];
  const probeWord = probe.id === "vocabulary" ? (extractWords(text)[0] || "") : "";

  useEffect(()=>{
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  },[done]);

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    setPendingQuality(quality);
    setPhase("probe");
    setShowText(false); // hide text when moving to probe
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     page.pageId || page._id || String(pageIdx),
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setPageIdx(i => i + 1);
      setShowText(false);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = pages.length>0?Math.round((correct/pages.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={pages.length} childName={childName} bookTitle={bookTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setPageIdx(0); setShowText(false); setHistory([]); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:m.bg,display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Picture Talk" subtitle={childName} color={m.color} current={pageIdx+1} total={pages.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px",gap:14}}>

        {phase === "rate" && (
          <>
            <div style={{fontSize:13,color:m.color,fontWeight:700,textAlign:"center"}}>
              👀 Look at this picture — what do you see?
            </div>
            {/* Image only — no text */}
            <PageImage page={page} language={language} height={300} showText={false}/>
            {text && (
              <button onClick={()=>setShowText(s=>!s)}
                style={{background:"white",border:`1.5px solid ${m.color}`,borderRadius:24,
                        padding:"7px 18px",fontSize:12,color:m.color,fontWeight:700,cursor:"pointer"}}>
                {showText?"🙈 Hide text":"👁 Reveal text"}
              </button>
            )}
            {showText && text && (
              <div style={{background:"white",border:`1.5px solid ${m.color}20`,borderRadius:12,
                           padding:"14px 18px",fontSize:15,fontFamily:"Georgia,serif",
                           lineHeight:1.8,color:"#2a1a0a",width:"min(520px,94vw)",
                           boxShadow:`0 2px 16px ${m.color}11`}}>
                {text}
                <button onClick={()=>speak(text,language)}
                  style={{display:"block",marginTop:8,background:m.bg,border:`1.5px solid ${m.color}`,
                          borderRadius:20,padding:"4px 14px",fontSize:11,color:m.color,fontWeight:700,cursor:"pointer"}}>
                  🔊 Read aloud
                </button>
              </div>
            )}
            <TeacherRating onRate={handleQualityRate} disabled={submRef.current} label="How well did the child describe the picture?"/>
          </>
        )}

        {phase === "probe" && (
          <>
            <PageImage page={page} language={language} height={140} showText={false} compact={true}/>
            <ProbeRound
              probe={probe}
              page={page}
              language={language}
              probeWord={probeWord}
              onRate={handleProbeRate}
              submitting={submRef.current}
            />
          </>
        )}

        {/* Progress dots */}
        <div style={{display:"flex",gap:7,marginTop:4}}>
          {pages.map((_,i)=>(
            <div key={i} style={{width:10,height:10,borderRadius:"50%",
                                 background:i<history.length?(history[i]?.correct?"#1B6B45":"#C0392B"):i===pageIdx?m.color:"#e2e8f0",
                                 transition:"background .3s"}}/>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 3 — RETELL THE STORY  (with probe after each checkpoint rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Three checkpoints: Beginning / Middle / Ending.
// Flow per checkpoint:
//   1. Storyboard filmstrip shown (section highlighted) → child retells that part
//   2. Teacher rates "how well did child retell?" (phase: "rate")
//      → stores quality, moves to "probe" phase
//   3. Section page thumbnail + ProbeRound shown
//   4. Teacher rates probe → both posted with signals[], advance to next checkpoint
//
function RetellMode({ pages, language, sessionId, getAuthHeader, childName, bookTitle, onDone }) {
  const m            = MODES[2];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const [showNotes,      setShowNotes]      = useState(false);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);
  const [cpIdx,   setCpIdx]   = useState(0);
  const [history, setHistory] = useState([]);

  const checkpoints = [
    { id:"beginning", label:"Beginning", icon:"🌅", hint:"How does the story start?" },
    { id:"middle",    label:"Middle",    icon:"🌿", hint:"What happens in the middle?" },
    { id:"ending",    label:"Ending",    icon:"🌇", hint:"How does the story end?" },
  ];

  const done  = cpIdx >= checkpoints.length;
  const cp    = checkpoints[cpIdx];
  const third = Math.ceil(pages.length / 3);
  const probe = PROBES[cpIdx % PROBES.length];

  // Representative page for this checkpoint section (for probe thumbnail)
  const sectionPageIdx = Math.min(cpIdx * third, pages.length - 1);
  const sectionPage    = pages[sectionPageIdx];
  const sectionText    = getPageText(sectionPage, language);
  const probeWord      = probe.id === "vocabulary" ? (extractWords(sectionText)[0] || "") : "";

  useEffect(()=>{
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  },[done]);

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     cp.id,
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setCpIdx(i => i + 1);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = checkpoints.length>0?Math.round((correct/checkpoints.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={checkpoints.length} childName={childName} bookTitle={bookTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setCpIdx(0); setHistory([]); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:m.bg,display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Retell the Story" subtitle={childName} color={m.color} current={cpIdx+1} total={checkpoints.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px",gap:16}}>

        {phase === "rate" && (
          <>
            {/* Checkpoint prompt */}
            <div style={{background:"white",border:`2.5px solid ${m.color}`,borderRadius:16,
                         padding:"16px 22px",width:"min(580px,96vw)",
                         boxShadow:`0 4px 24px ${m.color}22`}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
                <span style={{fontSize:28}}>{cp.icon}</span>
                <div>
                  <div style={{fontSize:10,fontWeight:800,color:m.color,textTransform:"uppercase",letterSpacing:0.8}}>
                    Part {cpIdx+1} of {checkpoints.length}
                  </div>
                  <div style={{fontSize:17,fontWeight:800,color:"#1a2b49"}}>{cp.label}</div>
                </div>
              </div>
              <div style={{fontSize:14,color:"#555",fontStyle:"italic"}}>{cp.hint}</div>
            </div>

            {/* Storyboard filmstrip — current section highlighted */}
            <div style={{background:"white",borderRadius:14,padding:16,
                         width:"min(640px,96vw)",boxShadow:"0 2px 12px rgba(0,0,0,.06)"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#888",letterSpacing:0.8,marginBottom:10}}>
                📚 STORYBOARD
              </div>
              <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4}}>
                {pages.map((pg,i)=>{
                  const section  = Math.floor(i/third);
                  const isActive = section===cpIdx;
                  const src = resolveUrl(pg.imageUrl);
                  return (
                    <div key={i} style={{flexShrink:0,width:72,opacity:isActive?1:0.35,transition:"opacity .3s"}}>
                      <div style={{height:52,border:`2px solid ${isActive?m.color:"#dce3f5"}`,
                                   borderRadius:7,overflow:"hidden",background:"#f8f9ff"}}>
                        {src
                          ? <img src={src} alt="" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                          : <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",fontSize:18}}>📄</div>
                        }
                      </div>
                      <div style={{fontSize:8,textAlign:"center",color:isActive?m.color:"#aaa",fontWeight:700,marginTop:2}}>p{i+1}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{fontSize:12,color:"#8a9ab5",fontWeight:600,textAlign:"center",width:"min(500px,94vw)"}}>
              Teacher: listen to the child retell this part, then rate how well they remembered it
            </div>
            <TeacherRating onRate={handleQualityRate} disabled={submRef.current} label={`Rate retelling: ${cp.label}`}/>
          </>
        )}

        {phase === "probe" && (
          <>
            <div style={{fontSize:11,color:"#2E7D32",fontWeight:700,letterSpacing:.5,textTransform:"uppercase"}}>
              {cp.icon} {cp.label} — one more question:
            </div>
            <PageImage page={sectionPage} language={language} height={140} showText={false} compact={true}/>
            <ProbeRound
              probe={probe}
              page={sectionPage}
              language={language}
              probeWord={probeWord}
              onRate={handleProbeRate}
              submitting={submRef.current}
            />
          </>
        )}

        {/* Checkpoint progress */}
        <div style={{display:"flex",gap:10,marginTop:4}}>
          {checkpoints.map((c,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:5,opacity:i<=cpIdx?1:0.35}}>
              <div style={{width:10,height:10,borderRadius:"50%",
                           background:i<history.length?(history[i]?.correct?"#1B6B45":"#C0392B"):i===cpIdx?m.color:"#e2e8f0"}}/>
              <div style={{fontSize:10,fontWeight:700,color:i===cpIdx?m.color:"#aaa"}}>{c.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 4 — WORD HUNT  (with probe as slide-up panel after each page rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per page:
//   1. Target word announced + highlighted text shown → child finds the word
//   2. Teacher rates "did child find the word?" → stored, probe panel slides up
//   3. Probe shown as slide-up panel (word hunt UI stays behind, dimmed)
//   4. Teacher rates probe → review posted with signals[], advance to next page
//
function WordHuntMode({ pages, language, sessionId, getAuthHeader, childName, bookTitle, onDone }) {
  const m            = MODES[3];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const startMs      = useRef(Date.now());
  const pendingReview = useRef(null); // stored on rate, posted after probe
  const [showNotes,     setShowNotes]     = useState(false);
  const [showProbe,     setShowProbe]     = useState(false);
  const [probeSubmitting, setProbeSubmitting] = useState(false);

  const [pageIdx,  setPageIdx]  = useState(0);
  const [target,   setTarget]   = useState(null);
  const [history,  setHistory]  = useState([]);

  const page  = pages[pageIdx];
  const text  = getPageText(page, language);
  const words = extractWords(text);
  const done  = pageIdx >= pages.length && !showProbe;
  const probe = PROBES[pageIdx % PROBES.length];
  // Vocabulary probe uses the current hunt target word (natural cross-probe)
  const probeWord = probe.id === "vocabulary" ? (target || words[0] || "") : "";

  useEffect(()=>{
    const ws = extractWords(getPageText(pages[pageIdx], language));
    setTarget(ws.length>0 ? ws[Math.floor(Math.random()*Math.min(3,ws.length))] : null);
  },[pageIdx]);

  useEffect(()=>{
    if (pageIdx >= pages.length && !showProbe && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  },[pageIdx, showProbe]);

  // Teacher rates "did child find the word?" → store, show probe
  const handleRate = (quality) => {
    if (submRef.current || showProbe) return;
    pendingReview.current = {
      cardId:     page.pageId || page._id || String(pageIdx),
      quality,
      responseMs: Date.now() - startMs.current,
      prompted:   quality < 3,
    };
    setHistory(h => [...h, { correct: quality >= 3 }]);
    setShowProbe(true);
  };

  const handleProbeRate = async (probeLevel) => {
    if (probeSubmitting) return;
    setProbeSubmitting(true);
    try {
      if (sessionId && pendingReview.current) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          ...pendingReview.current,
          signals: [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    pendingReview.current = null;
    setProbeSubmitting(false);
    setShowProbe(false);
    setTimeout(() => {
      setPageIdx(i => i + 1);
      startMs.current = Date.now();
    }, 200);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = pages.length>0?Math.round((correct/pages.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={pages.length} childName={childName} bookTitle={bookTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setPageIdx(0); setHistory([]); setShowProbe(false); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  if (pageIdx >= pages.length) return null; // safety guard while probe dismisses

  const highlightedText = target && text
    ? text.replace(new RegExp(`(${target})`, "gi"),
        `<mark style="background:#FFF176;border-radius:3px;padding:0 2px;font-weight:900;">$1</mark>`)
    : text;

  return (
    <div style={{minHeight:"100vh",background:m.bg,display:"flex",flexDirection:"column"}}>
      <ModeBar icon={m.icon} title="Word Hunt" subtitle={childName} color={m.color} current={pageIdx+1} total={pages.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"16px",gap:14}}>

        {/* Word Hunt UI — dimmed while probe is showing */}
        <div style={{opacity:showProbe?0.3:1,transition:"opacity .25s",pointerEvents:showProbe?"none":"auto",
                     display:"flex",flexDirection:"column",alignItems:"center",gap:14,width:"100%"}}>

          {/* Target word */}
          <div style={{background:"white",border:`2.5px solid ${m.color}`,borderRadius:14,
                       padding:"14px 22px",display:"flex",alignItems:"center",gap:16,
                       width:"min(580px,96vw)",boxShadow:`0 4px 24px ${m.color}22`}}>
            <div style={{fontSize:10,fontWeight:800,color:m.color,textTransform:"uppercase",letterSpacing:0.8,flexShrink:0}}>
              Find the word:
            </div>
            <div style={{fontSize:24,fontWeight:900,color:m.color,flex:1}}>{target||"—"}</div>
            <button onClick={()=>target&&speak(target,language)}
              style={{background:m.bg,border:`1.5px solid ${m.color}`,borderRadius:20,
                      padding:"6px 14px",fontSize:12,color:m.color,fontWeight:700,cursor:"pointer",flexShrink:0}}>
              🔊 Say it
            </button>
          </div>

          {/* Word picker */}
          {words.length>0 && (
            <div style={{background:"white",borderRadius:12,padding:"12px 16px",
                         width:"min(580px,96vw)",border:"1.5px solid #e0e7f0"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#888",marginBottom:8,
                           textTransform:"uppercase",letterSpacing:0.8}}>
                Teacher: pick a different target word
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                {words.map(w=>(
                  <button key={w} onClick={()=>{setTarget(w);speak(w,language);}}
                    style={{padding:"5px 12px",
                            background:target===w?m.color:m.bg,
                            color:target===w?"white":m.color,
                            border:`1.5px solid ${m.color}`,borderRadius:20,
                            fontSize:12,fontWeight:700,cursor:"pointer"}}>
                    {w}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Highlighted text + image */}
          <div style={{width:"min(520px,94vw)",background:"white",borderRadius:20,
                       overflow:"hidden",boxShadow:"0 6px 32px rgba(0,0,0,.10)",
                       border:"2px solid #e0e7f0"}}>
            {resolveUrl(page?.imageUrl) && (
              <div style={{height:200,background:"#f8f9ff",display:"flex",
                           alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                <img src={resolveUrl(page.imageUrl)} alt=""
                  style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
              </div>
            )}
            {text && (
              <div style={{padding:"16px 20px",fontSize:16,fontFamily:"Georgia,serif",
                           lineHeight:2,color:"#2a1a0a",borderTop:"2px solid #f0e8d8",
                           background:"#fffdf8"}}
                dangerouslySetInnerHTML={{__html: highlightedText}}/>
            )}
            {!text && (
              <div style={{padding:24,textAlign:"center",color:"#aaa",fontSize:12}}>
                No text for this page in {LANGUAGES.find(l=>l.code===language)?.name}
              </div>
            )}
          </div>

          <div style={{fontSize:11,color:"#8a9ab5",fontWeight:600,textAlign:"center"}}>
            👆 After the child points to the word, rate their response below
          </div>
          <TeacherRating onRate={handleRate} disabled={submRef.current||showProbe} label="Did the child find the word?"/>
        </div>

        {/* Progress dots */}
        <div style={{display:"flex",gap:7,marginTop:4,opacity:showProbe?0.3:1,transition:"opacity .25s"}}>
          {pages.map((_,i)=>(
            <div key={i} style={{width:10,height:10,borderRadius:"50%",
                                 background:i<history.length?(history[i]?.correct?"#1B6B45":"#C0392B"):i===pageIdx?m.color:"#e2e8f0",
                                 transition:"background .3s"}}/>
          ))}
        </div>
      </div>

      {/* Probe panel — slides up from bottom */}
      {showProbe && (
        <div style={{position:"fixed",bottom:0,left:0,right:0,
                     background:"white",borderTop:`2.5px solid ${m.color}`,
                     borderRadius:"20px 20px 0 0",padding:"20px 24px 32px",
                     boxShadow:"0 -8px 40px rgba(0,0,0,.18)",
                     display:"flex",flexDirection:"column",alignItems:"center",
                     animation:"slideUp .28s cubic-bezier(.22,.68,0,1.2) both",
                     zIndex:50}}>
          <div style={{fontSize:9,fontWeight:800,color:m.color,textTransform:"uppercase",
                       letterSpacing:.8,marginBottom:8,alignSelf:"flex-start"}}>
            {history[history.length-1]?.correct ? "✅ Found it!" : "❌ Not quite"} — one more:
          </div>
          <ProbeRound
            probe={probe}
            page={page}
            language={language}
            probeWord={probeWord}
            onRate={handleProbeRate}
            submitting={probeSubmitting}
          />
        </div>
      )}
      <style>{`@keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETUP SCREEN  (unchanged from original)
// ══════════════════════════════════════════════════════════════════════════════
function SetupScreen({ getAuthHeader, onStart, linkedId }) {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [books,    setBooks]    = useState([]);
  const [childId,  setChildId]  = useState("");
  const [bookId,   setBookId]   = useState("");
  const [language, setLanguage] = useState("en");
  const [mode,     setMode]     = useState("read-aloud");
  const [loading,  setLoading]  = useState(true);
  const [starting, setStarting] = useState(false);
  const [search,   setSearch]   = useState("");
  const [err,      setErr]      = useState("");

  useEffect(()=>{
    (async()=>{
      try {
        const [cd,bd] = await Promise.all([
          apiFetch("GET","/children?limit=200",  null,getAuthHeader()),
          apiFetch("GET","/storybook?limit=100", null,getAuthHeader()),
        ]);
        const kids = Array.isArray(cd?.children)?cd.children:Array.isArray(cd)?cd:[];
        setChildren(kids);
        setBooks(bd?.storyBooks||[]);
        if (kids.length===1) setChildId(kids[0]._id);
        if (linkedId) setBookId(linkedId);
      } catch(e){ setErr(e.message); }
      setLoading(false);
    })();
  },[]);

  const filteredBooks  = books.filter(b=>!search||b.title.toLowerCase().includes(search.toLowerCase()));
  const selectedBook   = books.find(b=>b._id===bookId);
  const selectedMode   = MODES.find(m=>m.id===mode);
  const canStart       = childId && bookId;

  const start = async () => {
    if (!canStart) return;
    setStarting(true); setErr("");
    try {
      const data = await apiFetch("GET",`/storybook/${bookId}`,null,getAuthHeader());
      if (!data.success) throw new Error(data.message||"Failed to load story book");
      const full = data.storyBook;
      apiFetch("POST",`/storybook/${bookId}/use`,null,getAuthHeader()).catch(()=>{});
      const sd = await apiFetch("POST","/flashcard-sessions/log",{
        childId, moduleType:"storybook", moduleRef:bookId, setTitle:full.title,
        sessionNotes:`Mode: ${mode}`, durationMin:0,
        conductorRole: user?.role==="parent"?"parent":"teacher",
        sessionType:   user?.role==="parent"?"parent-led":"teacher-led",
      }, getAuthHeader());
      const sessionId = sd.session?._id || sd._id || null;
      onStart({ child:children.find(c=>c._id===childId), book:full, pages:full.pages||[], language, mode, sessionId });
    } catch(e){ setErr(e.message); }
    setStarting(false);
  };

  if (loading) return (
    <div style={{background:"#FFFDF5",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <Spinner/>
    </div>
  );

  return (
    <div style={{background:"#FFFDF5",minHeight:"100vh",padding:"40px 16px",fontFamily:"'Segoe UI',Arial,sans-serif"}}>
      <div style={{maxWidth:540,margin:"0 auto"}}>
        <div style={{marginBottom:28,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:8}}>📖</div>
          <div style={{fontFamily:"Georgia,serif",fontSize:26,fontWeight:900,color:"#8B4513",marginBottom:4}}>Story Book Session</div>
          <div style={{fontSize:13,color:"#888"}}>Interactive reading activities · one literacy probe per page</div>
        </div>

        {err&&<div style={{background:"#FDEDEC",color:"#C0392B",padding:"10px 14px",borderRadius:8,fontSize:12,marginBottom:16,fontWeight:600}}>{err}</div>}

        {/* Child */}
        <div style={{background:"white",borderRadius:14,padding:18,marginBottom:14,border:"1.5px solid #e0e7f0",boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Child *</label>
          {children.length===0
            ? <div style={{fontSize:12,color:"#aaa",textAlign:"center",padding:14}}>No children found</div>
            : <div style={{display:"flex",flexDirection:"column",gap:7,maxHeight:190,overflowY:"auto"}}>
                {children.map(c=>{
                  const age=c.dob?new Date().getFullYear()-new Date(c.dob).getFullYear():null;
                  const sel=childId===c._id;
                  return (
                    <div key={c._id} onClick={()=>setChildId(c._id)}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",
                              border:`2px solid ${sel?"#8B4513":"#dce3f5"}`,
                              borderRadius:9,background:sel?"#FFF8F0":"white",cursor:"pointer",transition:"all .15s"}}>
                      <div style={{width:30,height:30,borderRadius:"50%",background:"#8B4513",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:11,fontWeight:800,flexShrink:0}}>
                        {(c.name||"?")[0].toUpperCase()}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#8B4513"}}>{c.name}</div>
                        {age&&<div style={{fontSize:10,color:"#888"}}>{age} years old</div>}
                      </div>
                      {sel&&<span style={{color:"#8B4513",fontWeight:900,fontSize:16}}>✓</span>}
                    </div>
                  );
                })}
              </div>
          }
        </div>

        {/* Book selector */}
        <div style={{background:"white",borderRadius:14,padding:18,marginBottom:14,border:"1.5px solid #e0e7f0",boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Story Book *</label>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search books…"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:8,padding:"7px 11px",fontSize:12,outline:"none",marginBottom:10,boxSizing:"border-box"}}/>
          {filteredBooks.length===0
            ? <div style={{fontSize:12,color:"#aaa",textAlign:"center",padding:20}}>
                {books.length===0?"No story books found. Create one in the Story Book Builder.":"No books match your search."}
              </div>
            : <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:220,overflowY:"auto"}}>
                {filteredBooks.map(b=>{
                  const sel=bookId===b._id;
                  return (
                    <div key={b._id} onClick={()=>setBookId(b._id)}
                      style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:12,
                              border:`2px solid ${sel?"#8B4513":"#dce3f5"}`,
                              borderRadius:9,background:sel?"#FFF8F0":"white",cursor:"pointer",transition:"all .15s"}}>
                      <span style={{fontSize:20,flexShrink:0}}>📖</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#8B4513",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"Georgia,serif"}}>{b.title}</div>
                        <div style={{fontSize:10,color:"#888",marginTop:1}}>{b.subtitle||""}{b.usedCount?" · Read "+b.usedCount+"×":""}</div>
                      </div>
                      {sel&&<span style={{color:"#8B4513",fontWeight:900,fontSize:16}}>✓</span>}
                    </div>
                  );
                })}
              </div>
          }
        </div>

        {/* Language + mode */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div style={{background:"white",borderRadius:14,padding:16,border:"1.5px solid #e0e7f0"}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Reading Language</label>
            <select value={language} onChange={e=>setLanguage(e.target.value)} style={{width:"100%",padding:"9px 10px",border:"1.5px solid #dce3f5",borderRadius:8,fontSize:12,outline:"none",background:"white"}}>
              {LANGUAGES.map(l=><option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <div style={{background:"white",borderRadius:14,padding:16,border:"1.5px solid #e0e7f0"}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Activity Mode</label>
            <select value={mode} onChange={e=>setMode(e.target.value)} style={{width:"100%",padding:"9px 10px",border:"1.5px solid #dce3f5",borderRadius:8,fontSize:12,outline:"none",background:"white"}}>
              {MODES.map(m=><option key={m.id} value={m.id}>{m.icon} {m.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:20}}>
          {MODES.map(m=>{
            const isActive=mode===m.id;
            return (
              <div key={m.id} onClick={()=>setMode(m.id)}
                style={{padding:"14px 12px",background:isActive?m.bg:"white",
                        border:`2px solid ${isActive?m.color:"#dce3f5"}`,
                        borderRadius:12,cursor:"pointer",textAlign:"center",transition:"all .15s",
                        boxShadow:isActive?"0 4px 16px rgba(0,0,0,.08)":"none"}}>
                <div style={{fontSize:22,marginBottom:4}}>{m.icon}</div>
                <div style={{fontSize:11,fontWeight:800,color:isActive?m.color:"#555",marginBottom:2}}>{m.label}</div>
                <div style={{fontSize:9,color:"#aaa",lineHeight:1.4}}>{m.desc}</div>
                {isActive&&<div style={{marginTop:6,fontSize:10,color:m.color,fontWeight:800}}>✓ Selected</div>}
              </div>
            );
          })}
        </div>

        {selectedBook && selectedMode && (
          <div style={{background:"#FFF8F0",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#8B4513",fontWeight:600,display:"flex",gap:8,alignItems:"center",border:"1px solid #D4A96A"}}>
            <span style={{fontSize:16}}>📚</span>
            <span>"{selectedBook.title}" · {selectedMode.label} · one literacy probe per page</span>
          </div>
        )}

        <button onClick={start} disabled={!canStart||starting}
          style={{width:"100%",padding:"15px",background:canStart?"#8B4513":"#ccc",color:"white",border:"none",borderRadius:12,fontWeight:800,fontSize:15,cursor:canStart?"pointer":"not-allowed",boxShadow:canStart?"0 6px 24px rgba(139,69,19,.3)":"none",fontFamily:"Georgia,serif"}}>
          {starting?"Loading…":`▶ Start ${selectedMode?.label||"Session"}`}
        </button>
        <div style={{textAlign:"center",fontSize:11,color:"#aaa",marginTop:12}}>
          Reading sessions tracked per child · one literacy probe per page
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT  (unchanged)
// ══════════════════════════════════════════════════════════════════════════════
export default function StoryBookLearning() {
  const { getAuthHeader } = useAuth();
  const { state: navState } = useLocation();
  const [phase,    setPhase]    = useState("setup");
  const [activity, setActivity] = useState(null);

  const launch = (data) => { setActivity(data); setPhase("play"); };
  const exit   = async () => {
    if (navState?.workplanId && navState?.entryId) {
      try {
        await fetch(`${API}/workplan/${navState.workplanId}/entry/${navState.entryId}`, {
          method:"PATCH", headers:{"Content-Type":"application/json",...getAuthHeader()},
          body: JSON.stringify({status:"done"}),
        });
      } catch {}
    }
    setActivity(null); setPhase("setup");
  };

  if (phase==="setup") return <SetupScreen getAuthHeader={getAuthHeader} onStart={launch} linkedId={navState?.linkedId}/>;

  if (phase==="play" && activity) {
    const { child, book, pages, language, mode, sessionId } = activity;
    const childName = child?.name || "";
    const bookTitle = book?.title || "";
    const common = { pages, language, sessionId, getAuthHeader, childName, bookTitle, onDone:exit };

    switch (mode) {
      case "read-aloud":   return <ReadAloudMode   {...common}/>;
      case "picture-talk": return <PictureTalkMode {...common}/>;
      case "retell":       return <RetellMode      {...common}/>;
      case "word-hunt":    return <WordHuntMode     {...common}/>;
      default:
        return (
          <div style={{padding:40,textAlign:"center",color:"#8a9ab5",fontFamily:"Georgia,serif"}}>
            <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
            Unknown mode: <strong>{mode}</strong><br/>
            <button onClick={exit} style={{marginTop:20,padding:"10px 24px",background:"#8B4513",color:"white",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700}}>← Back</button>
          </div>
        );
    }
  }

  return null;
}